import { QueryClient } from '@tanstack/react-query'
import { createClient } from '@/shared/lib/supabase/client'
import type { User } from '@supabase/supabase-js'

// The signed-in user lives under one query key, read by useUser() in the React
// tree and by getCurrentUser() in imperative store code — both go through the
// single browser QueryClient below, so they share one cache.
export const USER_QUERY_KEY = ['auth', 'user'] as const

export async function fetchUser(): Promise<User | null> {
  const supabase = createClient()
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined

export function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    // Server: a fresh client per request so cached data never leaks between users.
    return makeQueryClient()
  }
  // Browser: one shared client so the React tree (useUser) and imperative store
  // code (getCurrentUser) read and write the same cache.
  browserQueryClient ??= makeQueryClient()
  return browserQueryClient
}

// Imperative accessor for non-React code — Zustand store actions can't call the
// useUser() hook, but they can read the same cached, validated user it surfaces.
// Returns instantly from cache once anything has loaded the user (staleTime is
// Infinity; the auth listener keeps it fresh), and dedupes concurrent callers
// into a single fetch on a cold cache. Replaces per-action supabase.auth.getUser().
export async function getCurrentUser(): Promise<User | null> {
  try {
    return await getQueryClient().ensureQueryData({
      queryKey: USER_QUERY_KEY,
      queryFn: fetchUser,
      staleTime: Infinity,
    })
  } catch {
    // Match the old supabase.auth.getUser() contract: a fetch failure reads as
    // "no user" rather than throwing, so callers keep their `if (!user)` guard
    // and a transient blip never becomes an unhandled rejection in a store action.
    return null
  }
}
